export type CanonicalTreeBrowserDeploymentContext =
  | 'production'
  | 'deploy-preview'
  | 'branch-deploy';

export type CanonicalTreeBrowserBuildAndDeploymentIdentityUnavailableReason =
  | 'APPLICATION_BUILD_IDENTITY_MISSING_OR_INVALID'
  | 'DEPLOYMENT_IDENTITY_MISSING_OR_INVALID'
  | 'SOURCE_COMMIT_IDENTITY_MISSING_OR_INVALID'
  | 'DEPLOYMENT_CONTEXT_MISSING_OR_INVALID'
  | 'DEPLOYMENT_CONTEXT_NOT_ELIGIBLE';

export type CanonicalTreeBrowserBuildAndDeploymentIdentity =
  | Readonly<{
      status:
        'AVAILABLE';

      buildId:
        string;

      deploymentId:
        string;

      commitRef:
        string;

      deploymentContext:
        CanonicalTreeBrowserDeploymentContext;

      source:
        'NETLIFY_BUILD_TIME_VITE_INJECTION';

      publicationEligibilityCandidate:
        true;

      containsSecrets:
        false;
    }>
  | Readonly<{
      status:
        'UNAVAILABLE';

      reason:
        CanonicalTreeBrowserBuildAndDeploymentIdentityUnavailableReason;

      source:
        'NETLIFY_BUILD_TIME_VITE_INJECTION';

      publicationEligibilityCandidate:
        false;

      containsSecrets:
        false;
    }>;

const MAX_IDENTITY_LENGTH =
  200;

const ELIGIBLE_CONTEXTS =
  new Set<CanonicalTreeBrowserDeploymentContext>([
    'production',
    'deploy-preview',
    'branch-deploy',
  ]);

const unavailable =
  (
    reason:
      CanonicalTreeBrowserBuildAndDeploymentIdentityUnavailableReason,
  ): CanonicalTreeBrowserBuildAndDeploymentIdentity =>
    Object.freeze({
      status:
        'UNAVAILABLE',

      reason,

      source:
        'NETLIFY_BUILD_TIME_VITE_INJECTION',

      publicationEligibilityCandidate:
        false,

      containsSecrets:
        false,
    });

const normalizeIdentity =
  (
    value:
      unknown,
  ): string | null => {
    if (
      typeof value !==
        'string'
    ) {
      return null;
    }

    const normalized =
      value.trim();

    if (
      normalized.length <
        1 ||
      normalized.length >
        MAX_IDENTITY_LENGTH
    ) {
      return null;
    }

    if (
      /[\u0000-\u001f\u007f]/u.test(
        normalized,
      )
    ) {
      return null;
    }

    return normalized;
  };

export const readCanonicalTreeBrowserBuildAndDeploymentIdentity =
  (): CanonicalTreeBrowserBuildAndDeploymentIdentity => {
    const buildId =
      normalizeIdentity(
        import.meta.env
          ?.VITE_BUILD_ID,
      );

    if (
      buildId ===
        null
    ) {
      return unavailable(
        'APPLICATION_BUILD_IDENTITY_MISSING_OR_INVALID',
      );
    }

    const deploymentId =
      normalizeIdentity(
        import.meta.env
          ?.VITE_DEPLOYMENT_ID,
      );

    if (
      deploymentId ===
        null
    ) {
      return unavailable(
        'DEPLOYMENT_IDENTITY_MISSING_OR_INVALID',
      );
    }

    const commitRef =
      normalizeIdentity(
        import.meta.env
          ?.VITE_COMMIT_REF,
      );

    if (
      commitRef ===
        null
    ) {
      return unavailable(
        'SOURCE_COMMIT_IDENTITY_MISSING_OR_INVALID',
      );
    }

    const rawContext =
      normalizeIdentity(
        import.meta.env
          ?.VITE_DEPLOYMENT_CONTEXT,
      );

    if (
      rawContext ===
        null
    ) {
      return unavailable(
        'DEPLOYMENT_CONTEXT_MISSING_OR_INVALID',
      );
    }

    if (
      !ELIGIBLE_CONTEXTS.has(
        rawContext as
          CanonicalTreeBrowserDeploymentContext,
      )
    ) {
      return unavailable(
        'DEPLOYMENT_CONTEXT_NOT_ELIGIBLE',
      );
    }

    return Object.freeze({
      status:
        'AVAILABLE',

      buildId,
      deploymentId,
      commitRef,

      deploymentContext:
        rawContext as
          CanonicalTreeBrowserDeploymentContext,

      source:
        'NETLIFY_BUILD_TIME_VITE_INJECTION',

      publicationEligibilityCandidate:
        true,

      containsSecrets:
        false,
    });
  };
